import { classNames } from '@dimjs/utils';
import { sleep } from '@flatbiz/utils';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { CSSProperties, forwardRef, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { getScrollParent } from '../utils/get-scroll-parent';
import './style.less';
import { rubberbandIfOutOfBounds } from './utils/rubberband';

const classPrefix = `v-pull-to-refresh`;

export type PullStatus = 'pulling' | 'canRelease' | 'refreshing' | 'complete';

export type NativePullToRefreshProps = {
  onRefresh: () => Promise<any>;
  pullingText?: ReactNode;
  canReleaseText?: ReactNode;
  refreshingText?: ReactNode;
  completeText?: ReactNode;
  completeDelay?: number;
  headHeight?: number;
  threshold?: number;
  renderText?: (status: PullStatus) => ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode | ReactNode[];
};

export type NativePullToRefreshRefApi = {
  scrollToTop: () => void;
};

export const NativePullToRefresh = forwardRef<NativePullToRefreshRefApi, NativePullToRefreshProps>(
  (props, ref) => {
    const headHeight = props.headHeight ?? 40;
    const threshold = props.threshold ?? 50;

    const [status, setStatus] = useState<PullStatus>('pulling');

    const [springStyles, api] = useSpring(() => ({
      from: { height: 0 },
      config: {
        tension: 300,
        friction: 30,
        clamp: true,
      },
    }));

    const elementRef = useRef<HTMLDivElement>(null);

    const pullingRef = useRef(false);

    const doRefresh = async () => {
      api.start({ height: headHeight });
      setStatus('refreshing');
      try {
        await props.onRefresh();
        setStatus('complete');
        if (props.completeDelay && props.completeDelay > 0) {
          await sleep(props.completeDelay);
        }
      } catch (e) {
        // setStatus('pulling');
      }

      api.start({
        to: async (next) => {
          await next({ height: 0 });
          await next({ height: 0 });
          setStatus('pulling');
        },
      });
    };

    useDrag(
      (state) => {
        if (status === 'refreshing' || status === 'complete') return;

        const { event } = state;

        if (state.last) {
          pullingRef.current = false;
          if (status === 'canRelease') {
            void doRefresh();
          } else {
            api.start({ height: 0 });
          }
          return;
        }

        const [, y] = state.movement;
        if (state.first) {
          const element = elementRef.current;
          if (!element) return;
          const scrollParent = getScrollParent(element) as any;
          if (!scrollParent) return;
          const top = 'scrollTop' in scrollParent ? scrollParent.scrollTop : scrollParent.pageYOffset;
          if (top <= 0 && y > 0) {
            pullingRef.current = true;
          }
        }

        if (!pullingRef.current) return;

        if (typeof event.cancelable !== 'boolean' || event.cancelable) {
          event.preventDefault();
        }
        event.stopPropagation();
        const height = Math.max(rubberbandIfOutOfBounds(y, 0, 0, headHeight * 5, 0.5), 0);
        api.start({ height });
        setStatus(height > threshold ? 'canRelease' : 'pulling');
      },
      {
        pointer: { touch: true },
        axis: 'y',
        target: elementRef,
        eventOptions: { passive: false },
      },
    );

    const renderStatusText = () => {
      if (props.renderText) {
        return props.renderText?.(status);
      }

      if (status === 'pulling') return props.pullingText as string;
      if (status === 'canRelease') return props.canReleaseText as string;
      if (status === 'refreshing') return props.refreshingText as string;
      if (status === 'complete') return props.completeText as string;
      return '';
    };

    useImperativeHandle(ref, () => {
      return {
        scrollToTop: () => {
          const element = getScrollParent(elementRef.current as Element) as Element;
          element.scrollTo({
            top: 0,
            behavior: 'auto',
          });
        },
      };
    });

    return (
      <animated.div ref={elementRef} className={classNames(classPrefix, props.className)} style={props.style}>
        <animated.div style={springStyles} className={`${classPrefix}-head`}>
          <div className={`${classPrefix}-head-content`}>{renderStatusText()}</div>
        </animated.div>
        <div className={`${classPrefix}-content`}>{props.children}</div>
      </animated.div>
    );
  },
);

NativePullToRefresh.defaultProps = {
  pullingText: '下拉刷新',
  canReleaseText: '释放刷新',
  refreshingText: '加载中...',
  completeText: '刷新成功',
  completeDelay: 500,
};
